<script setup lang="ts">
import type { FactoryArg } from 'imask'

/**
 * Date typed by hand as ДД.ММ.ГГГГ (with `time`: ДД.ММ.ГГГГ ЧЧ:ММ) instead of the browser's
 * calendar. The model is what `<input type="date">` / `datetime-local` used to give:
 * `YYYY-MM-DD` / `YYYY-MM-DDTHH:mm`, or '' while the text is not a real date.
 * `invalid` tells a half-typed or impossible date from an empty field.
 */
defineOptions({ inheritAttrs: false })

const props = defineProps<{
  modelValue?: string
  time?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'update:invalid': [value: boolean]
}>()

const MIN_YEAR = 1900
const MAX_YEAR = 2100

const maskOptions = computed<FactoryArg>(() => ({ mask: props.time ? '00.00.0000 00:00' : '00.00.0000' }))
const length = computed(() => props.time ? 16 : 10)

function pad(value: number, size = 2): string {
  return String(value).padStart(size, '0')
}

function isoToText(iso: string | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(iso ?? '')
  if (!match) return ''
  const [, year, month, day, hour, minute] = match
  const date = `${day}.${month}.${year}`
  return props.time && hour !== undefined ? `${date} ${hour}:${minute}` : date
}

function textToIso(text: string): string {
  if (text.length !== length.value) return ''
  const [datePart, timePart] = text.split(' ')
  const [day, month, year] = datePart!.split('.').map(Number) as [number, number, number]
  const [hour, minute] = props.time ? timePart!.split(':').map(Number) as [number, number] : [0, 0]
  if (year < MIN_YEAR || year > MAX_YEAR) return ''
  const date = new Date(year, month - 1, day, hour, minute)
  const real = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    && date.getHours() === hour && date.getMinutes() === minute
  if (!real) return ''
  const iso = `${pad(year, 4)}-${pad(month)}-${pad(day)}`
  return props.time ? `${iso}T${pad(hour)}:${pad(minute)}` : iso
}

const text = ref(isoToText(props.modelValue))

// An outside change (a loaded profile, a picked day) replaces the text; the user's own typing does not echo back.
watch(() => props.modelValue, (value) => {
  if ((value ?? '') !== textToIso(text.value)) text.value = isoToText(value)
})

function onText(next: string) {
  text.value = next
  const iso = textToIso(next)
  if (iso !== (props.modelValue ?? '')) emit('update:modelValue', iso)
  emit('update:invalid', next !== '' && iso === '')
}
</script>

<template>
  <UiMaskProvider
    :mask-options="maskOptions"
    :model-value="text"
    @update:model-value="onText"
  >
    <UiInput
      v-bind="$attrs"
      :model-value="text"
      inputmode="numeric"
      autocomplete="off"
      :placeholder="time ? 'ДД.ММ.ГГГГ ЧЧ:ММ' : 'ДД.ММ.ГГГГ'"
    />
  </UiMaskProvider>
</template>
